import { rings } from '@/game/engine/projection';

/**
 * The curved cave throat — five nested arcs receding to the vanishing point.
 *
 * One authored arc, drawn five times at different scales. That is why the curve
 * only has to be right once, and why changing the cave's silhouette is a one-path
 * edit rather than five.
 *
 * Rings are stroked-and-filled: the fill is the rock face, the stroke is the lit
 * leading edge that makes each ring read as a separate lip of stone rather than a
 * flat gradient.
 *
 * The rock itself is a **tiled Blender render** (`/wall-tile.webp`, from
 * tools/blender/build_walls.py) — but it is tiled by `Corridor` as a CSS
 * background, NOT by an SVG `<pattern>` here, and the rings are translucent so it
 * shows through them.
 *
 * That was a deliberate retreat. A per-ring `<pattern>` is the prettier idea,
 * because it can scale the texture with each ring and give texture-gradient depth.
 * Rasterised, it showed a light grid along every pattern boundary — and the tile is
 * provably seamless (see build_walls.py --mosaic), so those lines came from the
 * pattern machinery, not the art. The overlap trick that usually cures it did not.
 *
 * The deciding factor was not which looked better, but which failure mode is
 * knowable: SVG pattern edge behaviour varies by rasteriser and the target is an
 * Android WebView I cannot rasterise from here, whereas `background-repeat` tiles
 * on exact pixel boundaries everywhere. Shipping the version whose seams I could
 * not test would have been shipping a guess.
 *
 * So the depth cue here is what it always was — ring tint and ring opacity — and
 * the texture is uniform across depth.
 *
 * `fillOpacity` and the void rect's opacity are not taste. They were solved for:
 * the tile is multiplied by a dark `zone.tint`, then dimmed twice more here, and at
 * 0.55/0.72 the rock came out at a luminance contrast of **2.7 out of 255** — a
 * texture that is technically present and cannot be seen. 0.22/0.38 with a brighter
 * tile puts it at ~19, which reads as stone while the cave stays dark (peak
 * luminance ~35). Raise either one and the wall goes back to being decoration
 * nobody can perceive; lower them and the cave stops feeling like a cave.
 */
export function ThroatRings({ lightPx }: { lightPx: number }) {
  const list = rings(lightPx);

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden
    >
      {/* Far darkness, but PARTIAL: the tiled wall lives behind this svg, and an
          opaque void would erase it. */}
      <rect width="100" height="100" fill="var(--cave-void)" opacity={0.22} />

      {list.map((r) => {
        /* Each ring spans `spread` of the viewport, centred on the vanishing point
           at 46% height — same constant the projection uses. */
        const w = 100 * r.spread;
        const h = 100 * r.spread;
        const x = 50 - w / 2;
        const y = 46 - h / 2;
        return (
          <g key={r.depth} opacity={r.opacity} transform={`translate(${x} ${y})`}>
            <path
              d={arc(w, h)}
              fill={r.tint}
              /* Translucent on purpose — this is the depth band, and the rock
                 texture is the thing underneath it. */
              fillOpacity={0.38}
              stroke="var(--cave-rim)"
              strokeWidth={0.35}
              strokeOpacity={0.55}
            />
          </g>
        );
      })}
    </svg>
  );
}

/**
 * One ring of the throat, in its own w×h box.
 *
 * Walls bow inward at the top (the ceiling arch) and flare slightly at the floor,
 * which is what stops the corridor reading as a plain oval tube.
 */
export function arc(w: number, h: number): string {
  const cx = w / 2;
  return [
    `M 0,${h}`,                                    // floor left
    `C 0,${h * 0.42} ${cx * 0.4},0 ${cx},0`,       // left wall up into the arch
    `C ${cx * 1.6},0 ${w},${h * 0.42} ${w},${h}`,  // arch down the right wall
    'Z',
  ].join(' ');
}
